import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Zap,
  Building2,
  UserPlus,
  LayoutDashboard,
  Code,
  Sparkles,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  {
    label: "XpressLinc",
    icon: Zap,
    color: "text-amber-400",
    bg: "bg-amber-400/10 group-hover:bg-amber-400/20",
    path: "/xpresslinc",
    tagline: "UPI & Switches",
    description: "High-throughput real-time orchestration"
  },
  {
    label: "BBPS Hub",
    icon: Building2,
    color: "text-blue-400",
    bg: "bg-blue-400/10 group-hover:bg-blue-400/20",
    path: "/product/bbps-ecosystem",
    tagline: "Bill Ecosystem",
    description: "Unified biller & agent network"
  },
  {
    label: "Merchant Onboarding",
    icon: UserPlus,
    color: "text-emerald-400",
    bg: "bg-emerald-400/10 group-hover:bg-emerald-400/20",
    path: "/onboarding-demos",
    tagline: "Digital KYC",
    description: "Seamless merchant & bank setup"
  },
  {
    label: "Admin Suite",
    icon: LayoutDashboard,
    color: "text-violet-400",
    bg: "bg-violet-400/10 group-hover:bg-violet-400/20",
    path: "/admin",
    tagline: "Configuration",
    description: "Core modules & access control"
  },
  {
    label: "API Registry",
    icon: Code,
    color: "text-rose-400",
    bg: "bg-rose-400/10 group-hover:bg-rose-400/20",
    path: "/xpresslinc/upi-switch",
    tagline: "Developer Specs",
    description: "Technical endpoints & sandbox"
  },
];

export default function QuickNavigator() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close when clicking outside panel
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  // Hidden on login and Bharat Connect sub-module pages (must be after all hooks)
  if (location.pathname === '/login' || location.pathname.startsWith('/product/bharat-connect-')) {
    return null;
  }

  return (
    <>
      {/* Click-outside backdrop when panel is open */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[140] bg-slate-950/20 backdrop-blur-[1px] hidden md:block"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Desktop Quick Access - Pinned Flush to the Right Edge */}
      <div
        ref={panelRef}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[150] hidden md:block select-none"
      >
        <AnimatePresence mode="wait">
          {!isOpen ? (
            /* Collapsed State: Sleek Pinned Vertical Tab (Does NOT block bottom-right corner) */
            <motion.button
              key="docked-tab"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 20, opacity: 0 }}
              whileHover={{ x: -3 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsOpen(true)}
              aria-label="Open Quick Demo Rails"
              title="Open Quick Demo Rails"
              className="flex flex-col items-center gap-2.5 py-3.5 px-2 bg-slate-900/95 dark:bg-slate-950/95 text-slate-300 hover:text-white rounded-l-xl border-y border-l border-slate-700/80 hover:border-primary/60 shadow-[-8px_0_24px_rgba(0,0,0,0.4)] backdrop-blur-xl transition-colors group cursor-pointer"
            >
              {/* Pulsing Live Operational Dot */}
              <div className="relative">
                <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.9)]" />
                <div className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" />
              </div>

              {/* Icon */}
              <Zap className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />

              {/* Vertical Text Label */}
              <span className="text-[10px] font-bold tracking-[0.16em] uppercase [writing-mode:vertical-rl] rotate-180 text-slate-400 group-hover:text-slate-200 transition-colors">
                Quick Demos
              </span>

              {/* Subtle Expand Indicator */}
              <div className="h-1 w-1 rounded-full bg-slate-600 group-hover:bg-primary transition-colors" />
            </motion.button>
          ) : (
            /* Expanded State: Compact Side Drawer Pinned to Right Edge */
            <motion.div
              key="expanded-drawer"
              initial={{ x: '100%', opacity: 0.8 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 32 }}
              className="w-80 bg-slate-950/95 text-white rounded-l-2xl border-y border-l border-slate-700/80 shadow-[-20px_0_50px_rgba(0,0,0,0.6)] backdrop-blur-3xl overflow-hidden p-4 flex flex-col gap-3.5"
            >
              {/* Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-800/80">
                <div className="flex items-center gap-2.5">
                  <div className="relative">
                    <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.9)]" />
                    <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-white">
                      Demo Ecosystem
                    </h3>
                    <p className="text-[10px] text-slate-400 font-medium">
                      5 Interactive Fintech Rails
                    </p>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close Quick Navigator"
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Navigation Items List */}
              <div className="flex flex-col gap-1.5">
                {NAV_ITEMS.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.label}
                      onClick={() => handleNavigate(item.path)}
                      className={cn(
                        "group w-full p-2.5 rounded-xl border border-transparent transition-all duration-200 flex items-center justify-between text-left",
                        isActive
                          ? "bg-slate-800/90 border-slate-700 shadow-sm"
                          : "hover:bg-slate-900/90 hover:border-slate-800"
                      )}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={cn("p-2 rounded-lg transition-colors", item.bg)}>
                          <item.icon className={cn("h-4 w-4", item.color)} strokeWidth={2.2} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-white group-hover:text-primary transition-colors truncate">
                              {item.label}
                            </span>
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 px-1.5 py-0.5 rounded bg-slate-800/60 border border-slate-700/50">
                              {item.tagline}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400 truncate mt-0.5 font-normal">
                            {item.description}
                          </p>
                        </div>
                      </div>

                      <ArrowRight className="h-3.5 w-3.5 text-slate-600 group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0 ml-2" />
                    </button>
                  );
                })}
              </div>

              {/* Footer hint */}
              <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-slate-500">
                <span>LincPay Sandbox</span>
                <span className="font-mono">Press ESC to dock</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Bottom Dock (Retained for mobile viewports) */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[150] md:hidden">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center gap-1 p-1.5 bg-slate-900/95 backdrop-blur-2xl border border-white/10 rounded-full shadow-2xl shadow-black/40"
        >
          {NAV_ITEMS.map((item, idx) => (
            <button
              key={idx}
              onClick={() => handleNavigate(item.path)}
              aria-label={item.label}
              title={item.label}
              aria-current={location.pathname === item.path ? 'page' : undefined}
              className={cn(
                "p-2.5 rounded-full transition-all active:scale-90",
                location.pathname === item.path ? "text-white bg-white/10 shadow-inner" : "text-slate-400"
              )}
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
            </button>
          ))}
          <div className="w-px h-5 bg-white/10 mx-1" />
          <button
            onClick={() => handleNavigate('/admin')}
            aria-label="Admin Suite"
            title="Admin Suite"
            className="p-2.5 rounded-full text-primary active:bg-primary/10"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          </button>
        </motion.div>
      </div>
    </>
  );
}
